import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { JOB_TYPE_LABELS, labelOf } from '../constants/jobOptions';
import {
  authLogin, authRegister, authVerify2FA, authLogout, verifyEmailCode, fetchProfile, fetchConversations,
  postApplication, fetchMyApplications, fetchSavedJobs, postSaveJob, deleteSaveJob,
} from '../services/api';

// Bewerbungen kommen als application-Objekte (id = Bewerbungs-ID) — auf Job-Form normalisieren
const normalizeApplication = (a) => ({
  ...a,
  id: a.job_id || a.id,
  application_id: a.id,
  company: a.company || a.company_name,
  type: labelOf(JOB_TYPE_LABELS, a.job_type) || a.type,
});

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [flashMsg, setFlashMsg] = useState(null);
  const [pendingVerificationEmail, setPendingVerificationEmail] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [savedJobs, setSavedJobs] = useState([]);
  const [appliedJobs, setAppliedJobs] = useState([]);
  const [notifications, setNotifications] = useState(0);

  const userType = user?.user_type || null;

  const clearSession = useCallback(() => {
    setUser(null);
    setSavedJobs([]);
    setAppliedJobs([]);
    setNotifications(0);
  }, []);

  // Session beim Start über httpOnly-Cookie wiederherstellen
  useEffect(() => {
    fetchProfile()
      .then(({ data }) => setUser(data))
      .catch(() => {})
      .finally(() => setSessionChecked(true));
  }, []);

  // Backend meldet 401 → Session serverseitig ungültig
  useEffect(() => {
    const onExpired = () => clearSession();
    window.addEventListener('auth:expired', onExpired);
    return () => window.removeEventListener('auth:expired', onExpired);
  }, [clearSession]);

  // Gespeicherte Jobs + Bewerbungen + Chat-Benachrichtigungen laden
  useEffect(() => {
    if (!user) return;
    // Login/Verifizierung liefern den User aus der Auth-Antwort, ohne
    // Bewerberprofil (profile_complete, school_status, …) – einmal nachladen
    if (!('profile_complete' in user)) refreshUser();
    if (user.user_type === 'youth') {
      fetchSavedJobs()
        .then(({ data }) => Array.isArray(data) && setSavedJobs(data))
        .catch(() => {});
      fetchMyApplications()
        .then(({ data }) => Array.isArray(data) && setAppliedJobs(data.map(normalizeApplication)))
        .catch(() => {});
    }
    fetchConversations()
      .then(({ data }) => {
        const convs = Array.isArray(data) ? data : data?.conversations || [];
        setNotifications(convs.filter(c => (c.unread_count || 0) > 0).length);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Schritt 1: Passwort prüfen → Server schickt 2FA-Code, bei unverifiziertem
  // Konto stattdessen den E-Mail-Bestätigungscode
  const login = async (email, password) => {
    setAuthLoading(true);
    setAuthError(null);
    try {
      const { data } = await authLogin({ email, password });
      return {
        twoFactorRequired: !!data.two_factor_required,
        verificationRequired: !!data.verification_required,
      };
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || 'Anmeldung fehlgeschlagen. Bitte prüfe deine Daten.';
      setAuthError(msg);
      throw err;
    } finally {
      setAuthLoading(false);
    }
  };

  // Schritt 2: E-Mail-Code bestätigen → Server setzt Session-Cookie
  const confirm2FA = async (email, code) => {
    setAuthLoading(true);
    setAuthError(null);
    try {
      const { data } = await authVerify2FA(email, code);
      setUser(data.user);
      setShowAuthModal(false);
    } catch (err) {
      const msg = err.response?.data?.error || 'Ungültiger oder abgelaufener Code.';
      setAuthError(msg);
      throw err;
    } finally {
      setAuthLoading(false);
    }
  };

  const register = async (firstName, lastName, email, password, type) => {
    setAuthLoading(true);
    setAuthError(null);
    try {
      await authRegister({
        first_name: firstName,
        last_name: lastName,
        email,
        password,
        user_type: type,
      });
      setShowAuthModal(false);
      setAuthMode('login');
      setPendingVerificationEmail(email);
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || 'Registrierung fehlgeschlagen. Bitte versuche es erneut.';
      setAuthError(msg);
      throw err;
    } finally {
      setAuthLoading(false);
    }
  };

  // E-Mail-Bestätigungscode einlösen: bestätigt das Konto und meldet an
  // (Server setzt das Session-Cookie). Wirft bei falschem/abgelaufenem Code.
  const confirmEmailCode = async (email, code) => {
    const { data } = await verifyEmailCode(email, code);
    if (data.already_verified) {
      setPendingVerificationEmail(null);
      openAuth('login');
      return;
    }
    setUser(data.user);
    setPendingVerificationEmail(null);
  };

  const logout = () => {
    authLogout().catch(() => {});
    clearSession();
  };

  const toggleSaveJob = async (job) => {
    if (!user) { openAuth('register'); return; }
    const isSaved = savedJobs.some(j => j.id === job.id);
    // Optimistic update
    if (isSaved) {
      setSavedJobs(prev => prev.filter(j => j.id !== job.id));
      deleteSaveJob(job.id).catch(() => setSavedJobs(prev => [...prev, job]));
    } else {
      setSavedJobs(prev => [...prev, job]);
      postSaveJob(job.id).catch(() => setSavedJobs(prev => prev.filter(j => j.id !== job.id)));
    }
  };

  const applyToJob = async (job) => {
    await postApplication(job.id);
    // Blockiert nichts, nur ein Hinweis nach dem Absenden
    if (user?.user_type === 'youth' && user.profile_complete === false) {
      setFlashMsg('Bewerbung gesendet. Tipp: Dein Profil ist noch unvollständig – im Profil kannst du Schule, Erfahrung und „Über mich“ ergänzen.');
    }
    // Refresh from server so ProfilePage shows the real application with status
    try {
      const { data } = await fetchMyApplications();
      if (Array.isArray(data)) setAppliedJobs(data.map(normalizeApplication));
    } catch {
      if (!appliedJobs.some(j => j.id === job.id)) {
        setAppliedJobs(prev => [...prev, { ...job, status: 'pending' }]);
      }
    }
  };

  const refreshUser = async () => {
    try {
      const { data } = await fetchProfile();
      setUser(data);
    } catch {
      // non-critical – session stays intact
    }
  };

  const openAuth = (mode = 'login') => {
    setAuthMode(mode);
    setAuthError(null);
    setShowAuthModal(true);
  };

  // Memoisiert, damit nicht jeder Render alle Consumer neu rendert
  const value = useMemo(() => ({
    user, userType, sessionChecked,
    login, confirm2FA, confirmEmailCode, register, logout, refreshUser,
    showAuthModal, setShowAuthModal,
    authMode, setAuthMode, openAuth,
    authLoading, authError, setAuthError,
    savedJobs, toggleSaveJob,
    appliedJobs, applyToJob,
    notifications,
    flashMsg, setFlashMsg,
    pendingVerificationEmail, setPendingVerificationEmail,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [
    user, userType, sessionChecked, showAuthModal, authMode, authLoading, authError,
    savedJobs, appliedJobs, notifications, flashMsg, pendingVerificationEmail,
  ]);

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
